// Colour themes and presenters for AI-generated templates.
// The theme names are the ones the AI chooses from (API: Services/AI/AIPlanSpec.cs).
//
// Each theme has:
//   dark1/dark2    dark scene background (gradient)
//   light1/light2  light scene background (gradient)
//   accent         main highlight (eyebrows, buttons, stat numbers)
//   accent2        second colour (icons and card tops on light scenes)
//   ink            headline colour on light scenes
//   muted          body text on light scenes
//   pale           body text on dark scenes
//   onAccent       text on an accent button
//   star           rating stars
//   warm1/warm2    "offer" scene background, with offerInk text, burst/burstInk badge,
//                  offerBtn/offerBtnInk button

export const THEMES = {
  navy_gold: {
    dark1: "#0b1f4b", dark2: "#1e3a8a", light1: "#f8fafc", light2: "#e6ecfa",
    accent: "#f5b83d", accent2: "#1e3a8a", ink: "#0b1f4b", muted: "#64748b", pale: "#cbd5e1",
    onAccent: "#0b1f4b", star: "#f5b83d",
    warm1: "#1e3a8a", warm2: "#0b1f4b", offerInk: "#ffffff", burst: "#f5b83d", burstInk: "#0b1f4b",
    offerBtn: "#f5b83d", offerBtnInk: "#0b1f4b",
  },
  violet_coral: {
    dark1: "#1e0b4b", dark2: "#5b21b6", light1: "#f5f3ff", light2: "#ede9fe",
    accent: "#fb7185", accent2: "#7c3aed", ink: "#1e0b4b", muted: "#6b7280", pale: "#ddd6fe",
    onAccent: "#ffffff", star: "#facc15",
    warm1: "#fb7185", warm2: "#f97316", offerInk: "#ffffff", burst: "#facc15", burstInk: "#1e0b4b",
    offerBtn: "#1e0b4b", offerBtnInk: "#ffffff",
  },
  teal_peach: {
    dark1: "#134e4a", dark2: "#0f766e", light1: "#fffbf5", light2: "#fdeedc",
    accent: "#fb923c", accent2: "#0f766e", ink: "#134e4a", muted: "#57534e", pale: "#ccfbf1",
    onAccent: "#ffffff", star: "#f59e0b",
    warm1: "#fdba74", warm2: "#fb923c", offerInk: "#134e4a", burst: "#0f766e", burstInk: "#ffffff",
    offerBtn: "#134e4a", offerBtnInk: "#ffffff",
  },
  emerald_lime: {
    dark1: "#052e16", dark2: "#166534", light1: "#f7fee7", light2: "#e7f8d4",
    accent: "#a3e635", accent2: "#16a34a", ink: "#14532d", muted: "#4b5563", pale: "#bbf7d0",
    onAccent: "#052e16", star: "#facc15",
    warm1: "#16a34a", warm2: "#065f46", offerInk: "#ffffff", burst: "#a3e635", burstInk: "#052e16",
    offerBtn: "#a3e635", offerBtnInk: "#052e16",
  },
  sunset_pink: {
    dark1: "#4a044e", dark2: "#9d174d", light1: "#fff7ed", light2: "#ffe4e6",
    accent: "#fb923c", accent2: "#db2777", ink: "#4a044e", muted: "#6b7280", pale: "#fbcfe8",
    onAccent: "#ffffff", star: "#fbbf24",
    warm1: "#f472b6", warm2: "#f97316", offerInk: "#ffffff", burst: "#fde047", burstInk: "#4a044e",
    offerBtn: "#4a044e", offerBtnInk: "#ffffff",
  },
  midnight_cyan: {
    dark1: "#020617", dark2: "#0c4a6e", light1: "#f0f9ff", light2: "#e0f2fe",
    accent: "#22d3ee", accent2: "#0284c7", ink: "#0f172a", muted: "#475569", pale: "#bae6fd",
    onAccent: "#020617", star: "#fbbf24",
    warm1: "#0891b2", warm2: "#4f46e5", offerInk: "#ffffff", burst: "#22d3ee", burstInk: "#020617",
    offerBtn: "#020617", offerBtnInk: "#ffffff",
  },
  rose_cream: {
    dark1: "#4c0519", dark2: "#881337", light1: "#fffaf5", light2: "#fdf2f8",
    accent: "#fda4af", accent2: "#be123c", ink: "#4c0519", muted: "#78716c", pale: "#fecdd3",
    onAccent: "#4c0519", star: "#f59e0b",
    warm1: "#fb7185", warm2: "#be123c", offerInk: "#ffffff", burst: "#fff1f2", burstInk: "#881337",
    offerBtn: "#4c0519", offerBtnInk: "#ffffff",
  },
  black_yellow: {
    dark1: "#0a0a0a", dark2: "#292524", light1: "#fafaf9", light2: "#f1f0ee",
    accent: "#facc15", accent2: "#171717", ink: "#0a0a0a", muted: "#57534e", pale: "#d6d3d1",
    onAccent: "#0a0a0a", star: "#facc15",
    warm1: "#facc15", warm2: "#f59e0b", offerInk: "#0a0a0a", burst: "#0a0a0a", burstInk: "#facc15",
    offerBtn: "#0a0a0a", offerBtnInk: "#facc15",
  },
};

export const themeFor = (name) => THEMES[name] || THEMES.navy_gold;

// Library characters with a talking mouth, chosen by the AI's "presenter" + "presenterStyle".
const POSES_A = "Angry,BoardShow,Cheer,Cry,FoldedArm,Gift,Idea,Love,Medal,Music,Pointing,Refuse,Sad,Sick,Sit,Stop,TabletShow,Wave,Work";
const POSES_B =
  "Angry,AngryWalk,AnswerSheet,Board,BooksHold,BoxHold,Call,Cheering,Clapping,Cough,Dab,FoldedArm,HoldingCake,HoldingStick," +
  "HoldingTray,Idea,Jump,LoudSpeaker,Love,LuggageHold,Medal,Music,NewsPaper,PC-Talking,Pointing,Presenting&Talking,Presenting," +
  "Relax,Running,Selfie,Show&Talk,Show,Show1,Show2,Show3,Show4,Sick,SideMusic,SideStanding,SideTalking,SideWalk,SideWave," +
  "SignBoard,SignBoard1,Sleeping,Sneezing,Stop,Talk,Thinking,ThumbsUp,TrophyJump,Typing&Talk,Typing,Umbrella,WatchingTime," +
  "Wave,WeightLifting,Yoga";

const PRESENTERS = {
  "woman-business": { id: "Female63", w: 175, h: 220, poses: POSES_A },
  "man-business": { id: "Male73", w: 172, h: 216, poses: POSES_A },
  "woman-casual": { id: "FatFemale2", w: 175, h: 220, poses: POSES_B },
  "man-casual": { id: "Male4", w: 175, h: 220, poses: POSES_B },
};

/** The presenter for the plan, or null for "none". */
export function presenterFor(plan) {
  if (!plan || plan.presenter === "none") return null;
  const who = plan.presenter === "man" ? "man" : "woman";
  const style = plan.presenterStyle === "casual" ? "casual" : "business";
  const p = PRESENTERS[who + "-" + style];
  const dirJson = `assets/StaticCharacterJson/Modern/${p.id}/${p.id}_`;
  const dirImg = `assets/StaticCharacterImages/Modern/${p.id}/${p.id}_`;
  return {
    ...p,
    file: dirJson + "Standing.json",
    image: dirImg + "Standing.png",
    poses: p.poses.split(",").map((pose) => ({
      poseFile: dirJson + pose + ".json",
      poseName: pose,
      poseImg: dirImg + pose + ".png",
    })),
  };
}
